import { lockScroll, unlockScroll } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/** Keep in sync with desktop breakpoints in assets/vitis-navigation.css */
const DESKTOP_NAV_MEDIA = '(min-width: 1150px)';
const desktopNavMediaQuery = window.matchMedia(DESKTOP_NAV_MEDIA);

/** Regions that may host scroll while mega menu / drawer scroll lock is active. */
const NAV_SCROLL_HOST_SELECTORS =
  '.vitis-mega__categories, .vitis-mega__panel, .vitis-subcol, .vitis-domains, .vitis-drawer__scroll, .vitis-drawer__localization-row, .vitis-header__localization, .country-selector-form__wrapper';

/**
 * Vitis header controller (`<vitis-header>`).
 *
 * Powers mobile drawer drilldown, desktop side-panel menu (click to open),
 * overlay, measured header offset for full-height columns / account popover,
 * and contextual drawer CTA updates.
 *
 * Desktop mega close keeps nested columns visible until the shell finishes
 * sliding out (`is-closing`), so all open layers exit as one drawer.
 */
class VitisHeader extends HTMLElement {
  /** @type {HTMLElement | null} */
  drawer = null;

  /** @type {HTMLElement | null} */
  overlay = null;

  /** @type {HTMLElement | null} */
  burger = null;

  /** @type {HTMLElement | null} */
  viewport = null;

  /** @type {HTMLElement | null} */
  ctaWrap = null;

  /** @type {HTMLAnchorElement | null} */
  ctaLink = null;

  /** @type {HTMLElement[]} */
  screens = [];

  /** @type {HTMLElement[]} */
  navItems = [];

  /** @type {string[]} */
  stack = ['root'];

  /** @type {HTMLElement | null} */
  lastFocused = null;

  /** @type {HTMLElement | null} */
  #megaLastFocused = null;

  /** @type {WeakMap<HTMLElement, AbortController>} */
  #panelEnterControllers = new WeakMap();

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #stickyTimeout = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ResizeObserver | null} */
  #headerGroupResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #megaCloseTimeout = null;

  /** @type {((event: TransitionEvent) => void) | null} */
  #megaCloseTransitionHandler = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #drawerCloseTimeout = null;

  /** @type {((event: TransitionEvent) => void) | null} */
  #drawerCloseTransitionHandler = null;

  #abortController = new AbortController();

  connectedCallback() {
    this.drawer = this.querySelector('.vitis-drawer');
    this.overlay = this.querySelector('.vitis-header__overlay');
    this.burger = this.querySelector('.vitis-header__burger');
    this.viewport = this.querySelector('.vitis-drawer__viewport');
    this.ctaWrap = this.querySelector('[data-vitis-cta]');
    this.ctaLink = this.querySelector('[data-vitis-cta-link]');
    this.screens = this.viewport
      ? /** @type {HTMLElement[]} */ (Array.from(this.viewport.querySelectorAll('.vitis-screen')))
      : [];
    this.navItems = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.vitis-nav__item--has-panel'))
    );

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('click', this.#onDocClick, { signal });

    this.#bindMegaClick(signal);
    this.#initSticky();
    this.#measureHeader();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      this.#measureHeader();
      if (entry?.borderBoxSize?.[0]) {
        const height = Math.round(entry.borderBoxSize[0].blockSize);
        document.body.style.setProperty('--header-height', `${height}px`);
      }
    });
    this.#resizeObserver.observe(this);
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      this.#headerGroupResizeObserver = new ResizeObserver(() => this.#measureHeader());
      this.#headerGroupResizeObserver.observe(headerGroup);
    }
    window.addEventListener('resize', this.#measureHeader, { signal });
    document.addEventListener('wheel', this.#onWheel, { passive: false, signal });
    document.addEventListener('touchmove', this.#onTouchMove, { passive: false, signal });
    desktopNavMediaQuery.addEventListener('change', this.#handleDesktopNavBreakpointChange);

    this.#syncDrawerScreenInert();
    this.#updateDrawerCta();
    this.#syncNavSurfaceForViewport();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#cancelMegaClose();
    this.#cancelDrawerClose();
    this.#resizeObserver?.disconnect();
    this.#headerGroupResizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    desktopNavMediaQuery.removeEventListener('change', this.#handleDesktopNavBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#stickyTimeout) {
      clearTimeout(this.#stickyTimeout);
      this.#stickyTimeout = null;
    }
    unlockScroll(this);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
  }

  /* -------------------------------------------------- sticky header (matches assets/header.js) */
  #initSticky() {
    const stickyMode = this.getAttribute('data-sticky');
    if (!stickyMode || stickyMode === 'never') return;

    this.#observeStickyPosition(stickyMode === 'always');

    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    });

    this.#intersectionObserver.observe(this);
  };

  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('data-sticky');
    if (!stickyMode || stickyMode === 'never') return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  /**
   * Tear down mega / drawer when the Vitis 1150px desktop surface no longer applies.
   * Independent of sticky mode (which uses Horizon's 990px scroll-container query).
   */
  #handleDesktopNavBreakpointChange = () => {
    this.#syncNavSurfaceForViewport();
  };

  #syncNavSurfaceForViewport() {
    if (desktopNavMediaQuery.matches) {
      this.#forceCloseDrawer();
    } else {
      this.#forceCloseMega();
    }
  }

  #forceCloseDrawer() {
    if (
      !this.classList.contains('is-drawer-open') &&
      !this.classList.contains('is-drawer-closing')
    ) {
      return;
    }
    this.#cancelDrawerClose();
    this.classList.remove('is-drawer-open', 'is-drawer-closing');
    this.burger?.setAttribute('aria-expanded', 'false');
    this.#resetScreens();
    this.#syncScrollLock();
    if (!this.#shouldLockScroll()) {
      this.overlay?.classList.remove('is-visible');
    }
  }

  #forceCloseMega() {
    if (
      !this.classList.contains('is-mega-open') &&
      !this.classList.contains('is-closing')
    ) {
      return;
    }
    this.#cancelMegaClose();
    this.#finalizeMegaClose();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#measureHeader();
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('data-sticky');
    if (!stickyMode || stickyMode === 'never') return;
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#stickyTimeout) {
      clearTimeout(this.#stickyTimeout);
      this.#stickyTimeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /* -------------------------------------------------- layout */
  #measureHeader = () => {
    this.#measureAnnouncementOffset();
    const rect = this.getBoundingClientRect();
    const headerBottom = Math.round(rect.bottom);
    this.style.setProperty('--vh-header-bottom', `${headerBottom}px`);
    this.style.setProperty('--shopify-account-dialog-position-top', `${headerBottom}px`);
    if (this.offsetHeight > 0) {
      document.body.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
    }
    this.#syncTransparentHeaderOffset();
  };

  /**
   * Mirror Horizon's --transparent-header-offset-boolean so the first main
   * section adds top padding (unless it opts out with disable-section-top-offset,
   * e.g. slideshow) while pulled under a transparent Vitis header.
   */
  #syncTransparentHeaderOffset() {
    const enabled = this.getAttribute('data-transparent-page') === 'true';
    document.body.style.setProperty('--transparent-header-offset-boolean', enabled ? '1' : '0');
  }

  #measureAnnouncementOffset() {
    const section = this.closest('.vitis-header-section');
    const headerGroup = document.getElementById('header-group');
    if (!(section instanceof HTMLElement) || !(headerGroup instanceof HTMLElement)) {
      this.style.setProperty('--vh-announcement-offset', '0px');
      return;
    }

    let offset = 0;
    for (const child of headerGroup.children) {
      if (child === section) break;
      if (child instanceof HTMLElement) {
        offset += child.getBoundingClientRect().height;
      }
    }

    this.style.setProperty('--vh-announcement-offset', `${Math.round(offset)}px`);
  };

  /* -------------------------------------------------- scroll lock */
  #shouldLockScroll() {
    if (this.classList.contains('is-drawer-open') || this.classList.contains('is-drawer-closing')) {
      return true;
    }
    if (
      desktopNavMediaQuery.matches &&
      (this.classList.contains('is-mega-open') || this.classList.contains('is-closing'))
    ) {
      return true;
    }
    return false;
  }

  #syncScrollLock() {
    if (this.#shouldLockScroll()) {
      lockScroll(this);
    } else {
      unlockScroll(this);
    }
  }

  /**
   * Nearest scrollable ancestor inside an allowed nav region.
   * Drawer localization expands inline (overflow visible), so wheel must fall through
   * to `.vitis-drawer__scroll` instead of stopping on a non-scrollable wrapper.
   * @param {Element} target
   * @returns {HTMLElement | null}
   */
  #findScrollHost(target) {
    let node = target;

    while (node instanceof Element) {
      if (node.matches(NAV_SCROLL_HOST_SELECTORS)) {
        const el = /** @type {HTMLElement} */ (node);
        if (el.scrollHeight > el.clientHeight + 1) {
          const { overflowY } = getComputedStyle(el);
          if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return el;
          }
        }
      }

      if (!node.parentElement || !this.contains(node.parentElement)) break;
      node = node.parentElement;
    }

    return null;
  }

  /**
   * Block page scroll while nav is open; allow wheel inside mega/drawer scroll regions.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (!this.#shouldLockScroll()) return;

    const target = event.target;
    if (!(target instanceof Element)) {
      event.preventDefault();
      return;
    }

    const scrollHost = this.#findScrollHost(target);

    if (scrollHost) {
      const { deltaY } = event;
      const atTop = scrollHost.scrollTop <= 0;
      const atBottom =
        scrollHost.scrollTop + scrollHost.clientHeight >= scrollHost.scrollHeight - 1;
      if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return;
    }

    event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    if (!this.#shouldLockScroll()) return;
    const target = event.target;
    if (!(target instanceof Element)) {
      event.preventDefault();
      return;
    }
    if (target.closest(`.vitis-drawer, ${NAV_SCROLL_HOST_SELECTORS}`)) {
      return;
    }
    event.preventDefault();
  };

  /* -------------------------------------------------- events */
  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const push = target.closest('[data-vitis-push]');
    if (push instanceof HTMLElement && this.contains(push)) {
      event.preventDefault();
      this.#pushScreen(push.getAttribute('data-vitis-push'));
      return;
    }

    const pop = target.closest('[data-vitis-pop]');
    if (pop instanceof HTMLElement && this.contains(pop)) {
      event.preventDefault();
      this.#popScreen();
      return;
    }

    const navTrigger = target.closest('.vitis-nav__item--has-panel > .vitis-nav__trigger');
    if (navTrigger instanceof HTMLElement && desktopNavMediaQuery.matches) {
      event.preventDefault();
      const navItem = navTrigger.closest('.vitis-nav__item--has-panel');
      if (navItem instanceof HTMLElement) {
        if (navItem.classList.contains('is-open')) {
          this.#closeMega();
        } else {
          this.#openMega(navItem, navTrigger);
        }
      }
      return;
    }

    const el = target.closest('[data-vitis]');
    if (!(el instanceof HTMLElement) || !this.contains(el)) return;

    switch (el.getAttribute('data-vitis')) {
      case 'open-drawer':
        event.preventDefault();
        this.#openDrawer();
        break;
      case 'close-drawer':
      case 'close':
        event.preventDefault();
        this.#closeDrawer();
        this.#closeMega();
        break;
      case 'toggle-cat':
        event.preventDefault();
        this.#toggleGroup(el, '.vitis-mega__cat');
        break;
      case 'toggle-sub':
        event.preventDefault();
        this.#toggleGroup(el, '.vitis-subcol__item');
        break;
    }
  };

  /** @param {MouseEvent} event */
  #onDocClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closeMega();
  };

  /** @param {AbortSignal} signal */
  #bindMegaClick(signal) {
    const plainNavTriggers = this.querySelectorAll(
      '.vitis-nav__item:not(.vitis-nav__item--has-panel) .vitis-nav__trigger'
    );
    plainNavTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.#closeMega(), { signal });
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} [trigger]
   */
  #openMega(item, trigger) {
    this.#cancelMegaClose();
    this.#measureHeader();
    requestAnimationFrame(() => this.#measureHeader());
    this.#megaLastFocused =
      trigger instanceof HTMLElement
        ? trigger
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    this.navItems.forEach((nav) => {
      nav.classList.remove('is-open');
      nav.querySelector('.vitis-nav__trigger')?.setAttribute('aria-expanded', 'false');
    });
    this.#clearNestedMegaState();
    item.classList.add('is-open');
    item.querySelector('.vitis-nav__trigger')?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-mega-open');
    this.classList.remove('is-closing');
    this.overlay?.classList.add('is-visible');
    this.#syncScrollLock();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.classList.contains('is-drawer-open') || this.classList.contains('is-drawer-closing')) {
        this.#closeDrawer();
      } else if (this.classList.contains('is-mega-open') || this.classList.contains('is-closing')) {
        this.#closeMega();
      }
      return;
    }
    if (event.key === 'Tab' && this.classList.contains('is-drawer-open')) {
      this.#trapFocus(event);
    }
  };

  /* -------------------------------------------------- drawer */
  #cancelDrawerClose() {
    if (this.#drawerCloseTimeout !== null) {
      clearTimeout(this.#drawerCloseTimeout);
      this.#drawerCloseTimeout = null;
    }
    if (this.drawer && this.#drawerCloseTransitionHandler) {
      this.drawer.removeEventListener('transitionend', this.#drawerCloseTransitionHandler);
    }
    this.#drawerCloseTransitionHandler = null;
    this.classList.remove('is-drawer-closing');
  }

  #finalizeDrawerClose() {
    this.#cancelDrawerClose();
    this.#syncScrollLock();
    if (!this.#shouldLockScroll()) {
      this.overlay?.classList.remove('is-visible');
    }
  }

  #openDrawer() {
    this.#cancelDrawerClose();
    this.lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.classList.add('is-drawer-open');
    this.overlay?.classList.add('is-visible');
    this.#syncScrollLock();
    this.burger?.setAttribute('aria-expanded', 'true');
    this.#resetScreens();
    this.drawer?.focus({ preventScroll: true });
  }

  #closeDrawer() {
    if (!this.classList.contains('is-drawer-open')) return;

    this.classList.remove('is-drawer-open');
    this.classList.add('is-drawer-closing');
    this.#syncScrollLock();
    if (!this.#shouldLockScroll()) this.overlay?.classList.remove('is-visible');
    this.burger?.setAttribute('aria-expanded', 'false');
    this.#resetScreens();
    this.lastFocused?.focus();

    const drawer = this.drawer;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const finish = () => {
      if (!this.classList.contains('is-drawer-closing')) return;
      this.#finalizeDrawerClose();
    };

    if (reducedMotion || !(drawer instanceof HTMLElement)) {
      finish();
      return;
    }

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== drawer || event.propertyName !== 'transform') return;
      drawer.removeEventListener('transitionend', onEnd);
      this.#drawerCloseTransitionHandler = null;
      finish();
    };

    this.#drawerCloseTransitionHandler = onEnd;
    drawer.addEventListener('transitionend', onEnd);
    this.#drawerCloseTimeout = setTimeout(finish, 500);
  }

  /**
   * @param {string | null | undefined} name
   * @returns {HTMLElement | undefined}
   */
  #getScreen(name) {
    if (!name) return undefined;
    return this.screens.find((screen) => screen.dataset.screen === name);
  }

  #resetScreens() {
    this.stack = ['root'];
    this.screens.forEach((screen) => screen.classList.remove('is-active', 'is-prev'));
    this.#getScreen('root')?.classList.add('is-active');
    this.#syncDrawerScreenInert();
    this.#updateDrawerCta();
  }

  /** @param {string | null} name */
  #pushScreen(name) {
    const target = this.#getScreen(name);
    if (!target || !name) return;
    const current = this.#getScreen(this.stack[this.stack.length - 1]);
    if (current) {
      current.classList.remove('is-active');
      current.classList.add('is-prev');
    }
    target.classList.remove('is-prev');
    target.classList.add('is-active');
    this.stack.push(name);
    this.#syncDrawerScreenInert();
    this.#updateDrawerCta();
    const back = target.querySelector('.vitis-drawer__back');
    if (back instanceof HTMLElement) back.focus({ preventScroll: true });
  }

  #popScreen() {
    if (this.stack.length <= 1) return;
    const current = this.#getScreen(this.stack.pop());
    const previous = this.#getScreen(this.stack[this.stack.length - 1]);
    current?.classList.remove('is-active');
    if (previous) {
      previous.classList.remove('is-prev');
      previous.classList.add('is-active');
    }
    this.#syncDrawerScreenInert();
    this.#updateDrawerCta();
  }

  #syncDrawerScreenInert() {
    this.screens.forEach((screen) => {
      if (screen.classList.contains('is-active')) {
        screen.removeAttribute('inert');
      } else {
        screen.setAttribute('inert', '');
      }
    });
  }

  #updateDrawerCta() {
    if (!this.ctaWrap || !this.ctaLink) return;
    const active = this.#getScreen(this.stack[this.stack.length - 1]);
    const label = active?.dataset.ctaLabel?.trim();
    const url = active?.dataset.ctaUrl?.trim();
    if (!label) {
      this.ctaWrap.hidden = true;
      return;
    }
    this.ctaWrap.hidden = false;
    this.ctaLink.textContent = label;
    this.ctaLink.href = url || '#';
  }

  /* -------------------------------------------------- mega menu */
  #clearNestedMegaState() {
    this.querySelectorAll('.vitis-mega__cat.is-open, .vitis-subcol__item.is-open').forEach((el) => {
      el.classList.remove('is-open');
      el.querySelector('button[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.vitis-mega__panel, .vitis-domains').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      this.#abortPanelEnter(el);
      el.classList.remove('is-open', 'is-entering');
    });
  }

  #clearOpenSubcols() {
    this.querySelectorAll('.vitis-subcol__item.is-open').forEach((sub) => {
      if (!(sub instanceof HTMLElement)) return;
      sub.classList.remove('is-open');
      sub.querySelector('button[aria-expanded]')?.setAttribute('aria-expanded', 'false');
      const domains = this.#panelForItem(sub, 'domains');
      if (domains) {
        this.#abortPanelEnter(domains);
        domains.classList.remove('is-open', 'is-entering');
      }
    });
  }

  /**
   * Resolve the sibling panel / domains column for a cat or subcol item.
   * @param {HTMLElement} item
   * @param {'panel' | 'domains'} kind
   * @returns {HTMLElement | null}
   */
  #panelForItem(item, kind) {
    if (kind === 'panel') {
      const id = item.getAttribute('data-vitis-panel-id');
      if (!id) return null;
      const panel = item.closest('.vitis-mega')?.querySelector(`.vitis-mega__panel[data-vitis-panel-id="${id}"]`);
      return panel instanceof HTMLElement ? panel : null;
    }
    const id = item.getAttribute('data-vitis-domains-id');
    if (!id) return null;
    const domains = item.closest('.vitis-mega__panel')?.querySelector(`.vitis-domains[data-vitis-domains-id="${id}"]`);
    return domains instanceof HTMLElement ? domains : null;
  }

  /** @param {HTMLElement} panel */
  #abortPanelEnter(panel) {
    const controller = this.#panelEnterControllers.get(panel);
    if (controller) {
      controller.abort();
      this.#panelEnterControllers.delete(panel);
    }
  }

  /**
   * Slide only the newly opened panel in from the left; existing columns stay put.
   * Sibling swaps skip this (instant), matching the pre-flicker-fix behavior.
   * @param {HTMLElement} panel
   */
  #enterPanel(panel) {
    this.#abortPanelEnter(panel);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.classList.remove('is-entering');
      return;
    }

    panel.classList.add('is-entering');

    const controller = new AbortController();
    this.#panelEnterControllers.set(panel, controller);

    /** @param {AnimationEvent} event */
    const onDone = (event) => {
      if (event.target !== panel) return;
      this.#abortPanelEnter(panel);
      panel.classList.remove('is-entering');
    };

    panel.addEventListener('animationend', onDone, { signal: controller.signal });
    panel.addEventListener('animationcancel', onDone, { signal: controller.signal });
  }

  /** @returns {HTMLElement | null} */
  #getOpenMegaShell() {
    const shell = this.querySelector('.vitis-nav__item--has-panel.is-open > .vitis-mega');
    return shell instanceof HTMLElement ? shell : null;
  }

  #cancelMegaClose() {
    if (this.#megaCloseTimeout !== null) {
      clearTimeout(this.#megaCloseTimeout);
      this.#megaCloseTimeout = null;
    }
    const shell = this.#getOpenMegaShell();
    if (shell && this.#megaCloseTransitionHandler) {
      shell.removeEventListener('transitionend', this.#megaCloseTransitionHandler);
    }
    this.#megaCloseTransitionHandler = null;
    this.classList.remove('is-closing');
    this.style.removeProperty('--vh-mega-close-shift');
  }

  #finalizeMegaClose() {
    if (this.#megaCloseTimeout !== null) {
      clearTimeout(this.#megaCloseTimeout);
      this.#megaCloseTimeout = null;
    }
    const shell = this.#getOpenMegaShell();
    if (shell && this.#megaCloseTransitionHandler) {
      shell.removeEventListener('transitionend', this.#megaCloseTransitionHandler);
    }
    this.#megaCloseTransitionHandler = null;

    this.navItems.forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('.vitis-nav__trigger')?.setAttribute('aria-expanded', 'false');
    });
    this.#clearNestedMegaState();
    this.classList.remove('is-mega-open', 'is-closing');
    this.style.removeProperty('--vh-mega-close-shift');
    this.#syncScrollLock();
    if (!this.#shouldLockScroll()) {
      this.overlay?.classList.remove('is-visible');
    }

    const restore = this.#megaLastFocused;
    this.#megaLastFocused = null;
    if (restore instanceof HTMLElement && this.contains(restore)) {
      restore.focus({ preventScroll: true });
    }
  }

  #closeMega() {
    if (this.classList.contains('is-closing')) return;
    if (!this.classList.contains('is-mega-open')) {
      this.#finalizeMegaClose();
      return;
    }

    const shell = this.#getOpenMegaShell();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Freeze slide distance to the full open width so L2/L3 exit with L1.
    const depth = this.querySelector('.vitis-subcol__item.is-open')
      ? 3
      : this.querySelector('.vitis-mega__cat--has-panel.is-open')
        ? 2
        : 1;
    this.style.setProperty(
      '--vh-mega-close-shift',
      `calc(var(--vh-desktop-panel-width, 380px) * ${depth})`
    );

    this.classList.add('is-closing');
    this.overlay?.classList.remove('is-visible');
    this.#syncScrollLock();

    if (reducedMotion || !(shell instanceof HTMLElement)) {
      this.#finalizeMegaClose();
      return;
    }

    const finish = () => {
      if (!this.classList.contains('is-closing')) return;
      this.#finalizeMegaClose();
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== shell || event.propertyName !== 'transform') return;
      shell.removeEventListener('transitionend', onEnd);
      this.#megaCloseTransitionHandler = null;
      finish();
    };

    this.#megaCloseTransitionHandler = onEnd;
    shell.addEventListener('transitionend', onEnd);
    this.#megaCloseTimeout = setTimeout(finish, 500);
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} itemSelector
   */
  #toggleGroup(trigger, itemSelector) {
    const item = trigger.closest(itemSelector);
    if (!(item instanceof HTMLElement)) return;
    const willOpen = !item.classList.contains('is-open');
    const parent = item.parentElement;
    const kind = itemSelector === '.vitis-mega__cat' ? 'panel' : 'domains';
    const hadOpenSibling = parent?.querySelector(`:scope > ${itemSelector}.is-open`) ?? null;

    // Always clear L3 when switching or closing an L2 category so a deep panel
    // cannot linger after opening a shallow (2-layer) category.
    if (itemSelector === '.vitis-mega__cat') {
      this.#clearOpenSubcols();
    }

    parent?.querySelectorAll(`:scope > ${itemSelector}.is-open`).forEach((sibling) => {
      if (!(sibling instanceof HTMLElement)) return;
      sibling.classList.remove('is-open');
      const siblingPanel = this.#panelForItem(sibling, kind);
      if (siblingPanel) {
        this.#abortPanelEnter(siblingPanel);
        siblingPanel.classList.remove('is-open', 'is-entering');
      }
      sibling.querySelector('button[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    });

    if (willOpen) {
      const panel = this.#panelForItem(item, kind);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Mark entering before is-open so the first paint uses the slide "from" frame.
      if (panel && !hadOpenSibling && !reducedMotion) {
        panel.classList.add('is-entering');
      }
      item.classList.add('is-open');
      panel?.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      if (panel && !hadOpenSibling) {
        this.#enterPanel(panel);
      }
    }
  }

  /* -------------------------------------------------- a11y */
  /** @param {KeyboardEvent} event */
  #trapFocus(event) {
    if (!this.drawer) return;
    const focusable = /** @type {HTMLElement[]} */ (
      Array.from(
        this.drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
      ).filter(
        (el) =>
          el instanceof HTMLElement &&
          !el.closest('[inert]') &&
          el.offsetParent !== null
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('vitis-header')) {
  customElements.define('vitis-header', VitisHeader);
}
